import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { FormContent, Heading } from '@dreryk/react-components';

import { AdditionalInformationTextarea } from '../../shared/AdditionalInformationTextarea';
import { CitoCheckbox } from '../../shared/CitoCheckbox';
import { resolveDiagnosesFormPart } from '../../shared/common.utils';
import { DiagnosesWithBodySideInteractiveTable } from '../../shared/DiagnosesWithBodySideInteractiveTable';
import { ExaminationsPerformedTextarea } from '../../shared/ExaminationsPerformedTextarea';
import { IllnessesAlreadyPassedTextarea } from '../../shared/IllnessesAlreadyPassedTextarea';
import { ObjectiveTextarea } from '../../shared/ObjectiveTextarea';
import { StyledReferralForm } from '../../shared/StyledReferralForm';
import { UnitNameAndAddressTextarea } from '../../shared/UnitNameAndAddressTextarea';
import { referralTranslationKeys } from '../../translations';
import { Icd9ProcedureSelector } from '../../shared/Icd9ProcedureField/Icd9ProcedureSelector';
import { HealthcareFacilityLocationSpecialtyField } from '../../shared/HealthcareFacilitySpecialtyField/HealthcareFacilityLocationSpecialtyField';

import {
   ReferralToRehabilitationFormBean,
   ReferralToRehabilitationFormController,
} from './ReferralToRehabilitationForm.types';

type ReferralToRehabilitationFormChildrenProps = Pick<
   ReferralToRehabilitationFormController,
   'handleSubmit' | 'handleSubmitDraft' | 'formState' | 'reset'
>;
type ReferralToRehabilitationFormProps = {
   readonly referralToRehabilitationDraft: ReferralToRehabilitationFormBean | null;
   readonly visitDiagnoses: VisitDiagnosis[] | undefined;
   readonly visitBeginDate: Date;
   readonly children: (childrenProps: ReferralToRehabilitationFormChildrenProps) => JSX.Element;
};

const getInitialValues = (
   referralToRehabilitationDraft: ReferralToRehabilitationFormBean | null,
   visitDiagnoses: VisitDiagnosis[] | undefined
): ReferralToRehabilitationFormBean => {
   return {
      healthcareFacilityLocationSpecialty: referralToRehabilitationDraft?.healthcareFacilityLocationSpecialty ?? null,
      cito: referralToRehabilitationDraft?.cito ?? false,
      diagnoses: resolveDiagnosesFormPart(referralToRehabilitationDraft?.diagnoses, visitDiagnoses),
      illnessesAlreadyPassed: referralToRehabilitationDraft?.illnessesAlreadyPassed ?? null,
      objective: referralToRehabilitationDraft?.objective ?? null,
      examinationsAlreadyPerformed: referralToRehabilitationDraft?.examinationsAlreadyPerformed ?? null,
      additionalInformation: referralToRehabilitationDraft?.additionalInformation ?? null,
      unitNameAndAddress: referralToRehabilitationDraft?.unitNameAndAddress ?? null,
      icd9Procedures: referralToRehabilitationDraft?.icd9Procedures ?? [],
   };
};

export const ReferralToRehabilitationForm = (props: ReferralToRehabilitationFormProps): JSX.Element => {
   const {
      register,
      formState,
      handleSubmit,
      handleSubmitDraft,
      reset,
      control,
   } = useFormController<ReferralToRehabilitationFormBean>(
      getInitialValues(props.referralToRehabilitationDraft, props.visitDiagnoses)
   );

   return (
      <>
         <Heading
            testId={'referralToRehabilitation'}
            content={referralTranslationKeys.referralType.REHABILITATION}
            type={'h1'}
         />
         <StyledReferralForm onSubmit={(event) => event.preventDefault()} noValidate>
            <FormContent testId={'referralToRehabilitation'}>
               <CitoCheckbox register={register} />
               <HealthcareFacilityLocationSpecialtyField register={register} specialtyType={'rehabilitationClinic'} />
               <UnitNameAndAddressTextarea register={register} />
               <DiagnosesWithBodySideInteractiveTable
                  register={register}
                  visitDiagnoses={props.visitDiagnoses}
                  dictionaryValidOn={props.visitBeginDate}
                  required
               />
               <Icd9ProcedureSelector
                  control={control}
                  dictionaryValidOn={props.visitBeginDate}
                  defaultValues={props.referralToRehabilitationDraft?.icd9Procedures}
               />
               <ObjectiveTextarea
                  register={register}
                  label={referralTranslationKeys.referralToRehabilitation.referralObjective}
                  placeholder={referralTranslationKeys.referralToRehabilitation.insertReferralObjective}
                  required
               />
               <IllnessesAlreadyPassedTextarea register={register} />
               <ExaminationsPerformedTextarea register={register} />
               <AdditionalInformationTextarea register={register} />
               {props.children({
                  handleSubmit: handleSubmit,
                  handleSubmitDraft: handleSubmitDraft,
                  formState: formState,
                  reset: reset,
               })}
            </FormContent>
         </StyledReferralForm>
      </>
   );
};
